import React from 'react';
import {
  isFilled,
  PROJECT_TYPES,
  planRoleLabel,
  scenarioGoalLabel,
} from '../state/model';
import { gatherInputs, computeRights, fmt } from '../engine/rights-engine';
import { computeProgramFit } from '../engine/program-fit';
import { computeLayers } from '../engine/layers';
import {
  collectDisclaimers,
  collectMissingData,
  buildExecSummary,
  existingSnapshot,
  collectConflicts,
} from '../engine/report';

// דוח — the developer report. Assembles the live calculation state + the
// selected scenario into the spec's section order (§9.2) with automatic
// disclaimers (§9.3). Printable to PDF via the browser.
export default function ReportTab({ project, updateProject }) {
  const scenarios = project.scenarios;
  const scenario = scenarios.find((s) => s.id === project.activeScenarioId) || scenarios[0] || null;

  const metrics = computeRights(gatherInputs(project));
  const fit = scenario ? computeProgramFit(project, scenario) : null;
  const disclaimers = collectDisclaimers(project, scenario, fit);
  const missing = collectMissingData(project, metrics);
  const execSummary = buildExecSummary(project, metrics, fit);
  const snap = existingSnapshot(project);
  const conflicts = collectConflicts(project);

  const get = (k) => metrics.find((m) => m.key === k);
  const mv = (k) => { const m = get(k); return m && m.status === 'ok' ? `${fmt(m.value, m.decimals)} ${m.unit}` : 'לא חושב'; };
  const projectTypeLabel = (PROJECT_TYPES.find((t) => t.id === project.project.projectType?.value) || {}).label || '—';
  const overflow = fit ? fit.rows.filter((r) => !r.missing && r.doesntFit > 0) : [];
  const alternatives = scenario ? scenarios.filter((s) => s.id !== scenario.id) : scenarios;
  const remArea = get('remainingArea');
  const overBuilt = remArea?.status === 'ok' && remArea.value < 0;
  const layered = computeLayers(project, metrics);

  const today = new Date().toLocaleDateString('he-IL');

  const selectScenario = (id) => updateProject((p) => { p.activeScenarioId = id; });

  // Sections are conditional, so numbers are assigned in render order — the
  // report never shows gaps in its numbering.
  let secCounter = 0;
  const nextNum = () => String(++secCounter);

  return (
    <div className="rd-report-wrap">
      <div className="rd-report-toolbar">
        {scenarios.length > 0 && (
          <label className="rd-target-units">
            תרחיש לדוח:
            <select className="rd-input rd-input-sm" value={scenario?.id || ''} onChange={(e) => selectScenario(e.target.value)}>
              {scenarios.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </label>
        )}
        <button type="button" className="rd-autofill-btn" onClick={() => window.print()}>הדפס / שמור PDF ⎙</button>
      </div>

      <div className="rd-report" dir="rtl">
        {/* 1 — cover */}
        <header className="rd-report-cover">
          <div className="rd-report-kicker">דוח יזמי · טיוטה</div>
          <h1>{project.project.name?.value || 'בדיקת זכויות'}</h1>
          <div className="rd-report-sub">
            {isFilled(project.property.address) ? project.property.address.value : 'כתובת לא הוזנה'}
            {isFilled(project.property.gush) && ` · גוש ${project.property.gush.value}`}
            {isFilled(project.property.helka) && ` · חלקה ${project.property.helka.value}`}
          </div>
          <div className="rd-report-date">הופק: {today}</div>
        </header>

        <Section num={nextNum()} title="תקציר מנהלים">
          <p>{execSummary}</p>
        </Section>

        <Section num={nextNum()} title="פרטי נכס ופרויקט">
          <dl className="rd-report-dl">
            <KV k="כתובת" v={project.property.address} />
            <KV k="גוש" v={project.property.gush} />
            <KV k="חלקה" v={project.property.helka} />
            <KV k='שטח מגרש (מ"ר)' v={project.property.parcelArea} />
            <KV k="רשות מקומית" v={project.property.localAuthority} />
            <KV k="ועדה מקומית" v={project.property.committee} />
            <KVText k="סוג פרויקט" v={projectTypeLabel} />
            <KV k="יזם" v={project.project.developer} />
            <KV k="מזמין" v={project.project.client} />
          </dl>
        </Section>

        <Section num={nextNum()} title="מקורות מידע">
          <p>{project.planStack.length} מקורות תכנוניים ב-Plan Stack, {project.permitTimeline.length} היתרים/בקשות, {project.documents.length} מסמכים בספרייה.</p>
        </Section>

        <Section num={nextNum()} title="תיאור מצב קיים">
          <p>{project.existingState.description?.value || 'לא נוצרה פסקת תיאור.'}</p>
          {(snap.builtArea != null || snap.units != null) && (
            <p className="rd-report-stat">
              {snap.builtArea != null && <span>שטח בנוי: {fmt(snap.builtArea)} מ"ר</span>}
              {snap.units != null && <span> · יח"ד קיימות: {snap.units}</span>}
            </p>
          )}
        </Section>

        <Section num={nextNum()} title="Plan Stack — מקורות ותפקידם">
          {project.planStack.length === 0 ? <p className="rd-report-empty">לא נמשכו תכניות.</p> : (
            <table className="rd-report-table">
              <thead><tr><th>מקור</th><th>תפקיד בחישוב</th><th>סטטוס</th></tr></thead>
              <tbody>
                {project.planStack.slice(0, 12).map((it) => (
                  <tr key={it.id}>
                    <td>{it.title.value || it.planRef}</td>
                    <td>{planRoleLabel(it.role.value)}</td>
                    <td>{it.type.value || it.planStatus || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>

        <Section num={nextNum()} title="זכויות מוקנות ופוטנציאל תכנוני">
          <table className="rd-report-table">
            <tbody>
              <MetricLine label='שטח כולל מותר' val={mv('allowedTotal')} />
              <MetricLine label='תכסית מותרת' val={mv('allowedCoverage')} />
              <MetricLine label='יח"ד מותר' val={mv('allowedUnits')} />
              <MetricLine label='גובה מותר' val={mv('maxHeight')} />
              <MetricLine label='קומות מותר' val={mv('maxStories')} />
              <MetricLine label='יתרת זכויות (שטח)' val={mv('remainingArea')} emphasize />
              <MetricLine label='יתרת יח"ד' val={mv('remainingUnits')} emphasize />
              {layered.vestedAdd > 0 && (
                <>
                  <MetricLine label='תוספות מוקנות משכבות (Modifier)' val={`${fmt(layered.vestedAdd)} מ"ר`} />
                  <MetricLine label='מעטפת מוקנית מלאה' val={`${fmt(layered.vestedFull)} מ"ר`} emphasize />
                  {layered.remainingFull != null && (
                    <MetricLine label='יתרה מול המעטפת המלאה' val={`${fmt(layered.remainingFull)} מ"ר`} emphasize />
                  )}
                </>
              )}
              {layered.potentialAdd > 0 && (
                <MetricLine label='פוטנציאל נוסף (לא מוקנה)' val={`${fmt(layered.potentialAdd)} מ"ר`} />
              )}
            </tbody>
          </table>
          {layered.vestedAdd > 0 && (
            <p className="rd-report-empty">תוספות השכבות הוזנו ידנית על ידי עורך הבדיקה ואינן חילוץ אוטומטי.</p>
          )}
        </Section>

        {scenario && (
          <>
            <Section num={nextNum()} title="מצב מוצע נבחר">
              <p><b>{scenario.name}</b> — מטרה: {scenarioGoalLabel(scenario.goal)}.</p>
              <p className="rd-report-stat">שטח מוצע כולל: {fmt(fit.proposedArea)} מ"ר</p>
            </Section>

            <Section num={nextNum()} title="השוואת מצב קיים מול מוצע">
              <p>
                קיים{snap.fromSummary ? ' (מתוך סיכום מצב בנוי)' : ' (עיקרי+שירות)'}: {fmt(snap.existingArea)} מ"ר
                {' → '}מוצע: {fmt(fit.proposedArea)} מ"ר
                {' ('}{fit.proposedArea - snap.existingArea >= 0 ? '+' : ''}{fmt(fit.proposedArea - snap.existingArea)} מ"ר).
              </p>
            </Section>

            <Section num={nextNum()} title="מה נכנס / מה לא נכנס מהפרוגרמה">
              <table className="rd-report-table">
                <thead><tr><th>רכיב</th><th>ביקש</th><th>נכנס</th><th>לא נכנס</th><th>הערה</th></tr></thead>
                <tbody>
                  {fit.rows.map((r) => (
                    <tr key={r.key}>
                      <td>{r.label}</td>
                      <td>{fmt(r.requested)}</td>
                      <td>{r.missing ? '—' : fmt(r.fits)}</td>
                      <td>{r.missing ? '—' : fmt(r.doesntFit)}</td>
                      <td>{r.note}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Section>
          </>
        )}

        {alternatives.length > 0 && (
          <Section num={nextNum()} title="תרחישים חלופיים">
            <ul className="rd-report-list">
              {alternatives.map((s) => <li key={s.id}>{s.name} — {scenarioGoalLabel(s.goal)}</li>)}
            </ul>
          </Section>
        )}

        <Section num={nextNum()} title="מגבלות וסיכונים">
          <ul className="rd-report-list">
            {overBuilt && (
              <li><b>המבנה הקיים חורג מזכויות תכנית הבסיס</b> בכ-{fmt(Math.abs(remArea.value))} מ"ר — ככל הנראה מומשו זכויות מתכניות מאוחרות (למשל תמ"א 38). חישוב יתרה מלא מול כלל השכבות טרם בוצע.</li>
            )}
            {conflicts.map((c, i) => (
              <li key={`c${i}`}><b>סתירה בין מקורות — {c.label}:</b> {c.note || 'הערכים מהמקורות אינם תואמים; נדרשת הכרעה ידנית.'}</li>
            ))}
            {overflow.map((r) => <li key={r.key}>{r.label}: חורג מהזכויות — {fmt(r.doesntFit)} מעבר למותר.</li>)}
            <li>מקור הזכויות הראשי טעון אימות מול האזור החל על החלקה (לא מסונן אוטומטית).</li>
            {overflow.length === 0 && !overBuilt && conflicts.length === 0 && <li>לא זוהו חריגות מהזכויות בפרוגרמה הנוכחית.</li>}
          </ul>
        </Section>

        <Section num={nextNum()} title="נתונים חסרים">
          {missing.length === 0 ? <p className="rd-report-empty">לא זוהו חוסרים קריטיים.</p> : (
            <table className="rd-report-table">
              <thead><tr><th>חסר</th><th>למה חשוב</th><th>איך משיגים</th><th>השפעה על החישוב</th></tr></thead>
              <tbody>
                {missing.map((t, i) => (
                  <tr key={i}><td>{t.item}</td><td>{t.why}</td><td>{t.how}</td><td>{t.impact}</td></tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>

        <Section num={nextNum()} title="דיסקליימרים">
          {disclaimers.length === 0 ? <p className="rd-report-empty">—</p> : (
            <ul className="rd-report-list">
              {disclaimers.map((d) => <li key={d.key}><b>{d.title}:</b> {d.text}</li>)}
            </ul>
          )}
        </Section>

        <Section num={nextNum()} title="נספחים וטבלאות">
          <p className="rd-report-empty">מטריצות מצב קיים ומוצע, Plan Stack מלא והיתרים — זמינים בלשוניות העבודה.</p>
        </Section>

        <footer className="rd-report-footer">
          הופק על ידי אורבניקס · טיוטה לבדיקה · כל הנתונים טעונים אימות מקצועי לפני קבלת החלטות.
        </footer>
      </div>
    </div>
  );
}

function Section({ num, title, children }) {
  return (
    <section className="rd-report-section">
      <h2><span className="rd-report-secnum">{num}</span> {title}</h2>
      {children}
    </section>
  );
}

function KV({ k, v }) {
  return <><dt>{k}</dt><dd>{isFilled(v) ? v.value : '—'}</dd></>;
}
function KVText({ k, v }) {
  return <><dt>{k}</dt><dd>{v || '—'}</dd></>;
}
function MetricLine({ label, val, emphasize }) {
  return (
    <tr className={emphasize ? 'rd-report-emph' : ''}>
      <td>{label}</td>
      <td className="rd-report-metricval">{val}</td>
    </tr>
  );
}
